import {
  type CarListItem,
  type CarListResponse,
  type CarStatus,
  formatEtaShort,
  formatMoney,
  formatSignedMoney,
  STATUS_LABEL,
} from '@telega/shared';
import { CarFront, ChevronDown, Megaphone, Plus, Search, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useCars } from '../api/cars';
import { useChannels } from '../api/channels';
import { uk } from '../i18n/uk';
import { cx } from '../lib/cx';
import { haptic } from '../telegram';
import {
  Button,
  Cell,
  EmptyState,
  ErrorState,
  FilterChip,
  ListSkeleton,
  Page,
  Section,
  StatusChip,
} from '../ui';

const t = uk.fleet;
const FILTERS: Array<CarStatus | 'all'> = [
  'all',
  'in_transit',
  'customs',
  'available',
  'reserved',
  'sold',
  'archived',
];

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

/** Collapsed "N авто в роботі ▾"; expands into currency / invested / margin (spec 6.3). */
function TotalsWidget({ totals }: { totals: CarListResponse['totals'] }) {
  const [open, setOpen] = useState(false);
  return (
    <Section>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => {
          haptic.light();
          setOpen((o) => !o);
        }}
        className="flex min-h-11 w-full items-center justify-between px-4 py-2.5 text-left"
      >
        <span className="text-[17px] font-semibold">{t.inWork(totals.activeCars)}</span>
        <ChevronDown
          className={cx('size-5 text-hint transition-transform', open && 'rotate-180')}
          aria-hidden
        />
      </button>
      {open && (
        <div className="px-4 pb-3">
          {totals.rows.length === 0 ? (
            <p className="text-[15px] text-hint">{t.totalsHint}</p>
          ) : (
            <>
              <table className="w-full text-[15px] tabular-nums">
                <thead>
                  <tr className="text-left text-[13px] text-hint">
                    <th className="pb-1 font-normal">{t.currency}</th>
                    <th className="pb-1 text-right font-normal">{t.invested}</th>
                    <th className="pb-1 text-right font-normal">{t.margin}</th>
                  </tr>
                </thead>
                <tbody>
                  {totals.rows.map((row) => (
                    <tr key={row.currency} className="border-t border-separator/50">
                      <td className="py-1.5">{row.currency}</td>
                      <td className="py-1.5 text-right">
                        {formatMoney(row.invested, row.currency)}
                      </td>
                      <td
                        className={cx(
                          'py-1.5 text-right font-semibold',
                          row.margin > 0
                            ? 'text-positive'
                            : row.margin < 0
                              ? 'text-destructive'
                              : '',
                        )}
                      >
                        {formatSignedMoney(row.margin, row.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-2 text-[13px] leading-snug text-hint">{t.totalsHint}</p>
            </>
          )}
        </div>
      )}
    </Section>
  );
}

const DOT: Record<CarListItem['channels'][number]['status'], string> = {
  posted: 'bg-link',
  manual_done: 'bg-link',
  queued: 'bg-hint',
  manual_pending: 'bg-st-customs',
  failed: 'bg-destructive',
  deleted: 'bg-hint/40',
};

function ChannelDots({ channels }: { channels: CarListItem['channels'] }) {
  if (channels.length === 0) return null;
  return (
    <span role="img" className="flex items-center gap-1" aria-label={`Каналів: ${channels.length}`}>
      {channels.slice(0, 5).map((c) => (
        <span key={c.channelId} className={cx('size-1.5 rounded-full', DOT[c.status])} />
      ))}
    </span>
  );
}

function Thumb({ url }: { url: string | null }) {
  return url ? (
    <img src={url} alt="" loading="lazy" className="size-14 shrink-0 rounded-lg object-cover" />
  ) : (
    <div className="flex size-14 shrink-0 items-center justify-center rounded-lg bg-hint/15 text-hint">
      <CarFront className="size-6" aria-hidden />
    </div>
  );
}

function CarRow({ car, onOpen }: { car: CarListItem; onOpen: () => void }) {
  return (
    <Cell
      onClick={onOpen}
      className={cx(
        // T7.3: rows off screen are not laid out or painted, so 300 cars scroll like 10.
        '[--sep-inset:84px] [content-visibility:auto] [contain-intrinsic-size:auto_92px]',
        (car.status === 'sold' || car.status === 'archived') && 'opacity-60',
      )}
      before={<Thumb url={car.mainPhotoUrl} />}
      title={`${car.make} ${car.model}`}
      multilineTitle
      // Year and city on their own line, the status under them: nothing is ever cut.
      subtitle={
        <span className="block">
          <span className="block">
            {car.year} · {car.city}
          </span>
          <span className="mt-1.5 flex items-center gap-2">
            <StatusChip
              status={car.status}
              {...(car.status === 'in_transit' && car.eta
                ? { suffix: formatEtaShort(car.eta) }
                : {})}
            />
            <ChannelDots channels={car.channels} />
          </span>
        </span>
      }
      multilineSubtitle
      align="top"
      after={
        <span className="flex flex-col items-end">
          <span className="font-semibold text-text tabular-nums">
            {formatMoney(car.priceUsd, 'USD')}
          </span>
          {car.oldPriceUsd !== null && car.oldPriceUsd > car.priceUsd && (
            <s className="text-[13px] tabular-nums">{formatMoney(car.oldPriceUsd, 'USD')}</s>
          )}
        </span>
      }
    />
  );
}

export function FleetPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<CarStatus | 'all'>('all');
  const [query, setQuery] = useState('');
  const q = useDebounced(query, 250);
  const cars = useCars(status, q);
  const channels = useChannels();

  // The chips count what the search finds; the fleet size ignores it.
  const counts = cars.data?.counts ?? {};
  const totalCars = cars.data?.fleetSize ?? 0;
  const noTelegramChannels =
    channels.isSuccess &&
    !channels.data.some((c) => c.type === 'tg_channel' || c.type === 'tg_group');
  const addCar = () => navigate('/cars/new');

  return (
    <Page
      title={uk.tabs.fleet}
      bottomInset="tabbar"
      titleAction={
        <button
          type="button"
          aria-label={t.add}
          onClick={addCar}
          className="flex size-9 items-center justify-center rounded-full bg-button text-button-text active:opacity-80"
        >
          <Plus className="size-5" />
        </button>
      }
    >
      {cars.isPending && <ListSkeleton rows={5} />}
      {cars.isError && <ErrorState message={cars.error.message} onRetry={() => cars.refetch()} />}

      {cars.isSuccess && totalCars === 0 && (
        <>
          {noTelegramChannels && <NoChannelsBanner onOpen={() => navigate('/channels')} />}
          <EmptyState
            icon={<CarFront className="size-12" />}
            title={t.emptyTitle}
            text={t.emptyText}
            action={
              <Button variant="primary" onClick={addCar}>
                {t.add}
              </Button>
            }
          />
        </>
      )}

      {cars.isSuccess && totalCars > 0 && (
        <>
          {noTelegramChannels && <NoChannelsBanner onOpen={() => navigate('/channels')} />}
          <TotalsWidget totals={cars.data.totals} />

          <div className="mx-4 mb-3 flex h-9 items-center gap-2 rounded-[10px] bg-hint/15 px-2.5">
            <Search className="size-4 shrink-0 text-hint" aria-hidden />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t.search}
              aria-label={t.search}
              className="min-w-0 flex-1 bg-transparent text-[16px] outline-none placeholder:text-hint"
            />
            {query && (
              <button
                type="button"
                aria-label="Очистити"
                onClick={() => setQuery('')}
                className="text-hint"
              >
                <X className="size-4" />
              </button>
            )}
          </div>

          <div className="mb-4 flex gap-2 overflow-x-auto px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {FILTERS.filter(
              (f) => f !== 'archived' || (counts.archived ?? 0) > 0 || status === 'archived',
            ).map((f) => (
              <FilterChip
                key={f}
                label={f === 'all' ? t.all : STATUS_LABEL[f]}
                count={counts[f] ?? 0}
                selected={status === f}
                onClick={() => {
                  haptic.selection();
                  setStatus(f);
                }}
              />
            ))}
          </div>

          {cars.data.items.length === 0 ? (
            <EmptyState
              icon={<Search className="size-10" />}
              title={t.nothingFound}
              text={t.nothingFoundText}
            />
          ) : (
            <Section className={cx(cars.isPlaceholderData && 'opacity-60 transition-opacity')}>
              {cars.data.items.map((car) => (
                <CarRow key={car.id} car={car} onOpen={() => navigate(`/cars/${car.id}`)} />
              ))}
            </Section>
          )}
        </>
      )}
    </Page>
  );
}

function NoChannelsBanner({ onOpen }: { onOpen: () => void }) {
  return (
    <Section>
      <Cell
        before={
          <div className="flex size-10 items-center justify-center rounded-full bg-button/12 text-button">
            <Megaphone className="size-5" aria-hidden />
          </div>
        }
        title={t.noChannelsTitle}
        subtitle={t.noChannelsText}
        multilineTitle
        chevron
        onClick={onOpen}
        className="[&_.truncate]:whitespace-normal"
      />
    </Section>
  );
}
